import {
  BookOpen, Clock3, FileText, Film, Layers, ListChecks, Moon, Puzzle, RefreshCw, Sparkles, Trophy,
} from 'lucide-react';

/** One place that knows what each kind of plan item is, so Today, Week and the
 * Roadmap all speak the same visual language. */
export const ITEM_META = {
  learn: { icon: Film, label: 'Watch', tone: 'text-violet-600 bg-violet-50 border-violet-200' },
  read: { icon: BookOpen, label: 'Read', tone: 'text-sky-600 bg-sky-50 border-sky-200' },
  practice: { icon: ListChecks, label: 'Practise', tone: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  flashcards: { icon: Layers, label: 'Flashcards', tone: 'text-amber-600 bg-amber-50 border-amber-200' },
  recall: { icon: Moon, label: 'Recall', tone: 'text-slate-500 bg-slate-100 border-slate-200' },
  catchup: { icon: RefreshCw, label: 'Catch-up', tone: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
  revision: { icon: Sparkles, label: 'Revise', tone: 'text-sky-700 bg-sky-50 border-sky-200' },
  final_revision: { icon: Trophy, label: 'Final push', tone: 'text-rose-600 bg-rose-50 border-rose-200' },
  sweep: { icon: Sparkles, label: 'Sweep', tone: 'text-fuchsia-600 bg-fuchsia-50 border-fuchsia-200' },
  mock: { icon: FileText, label: 'Mock', tone: 'text-rose-600 bg-rose-50 border-rose-200' },
  weekly_test: { icon: FileText, label: 'Weekly test', tone: 'text-rose-600 bg-rose-50 border-rose-200' },
  puzzle: { icon: Puzzle, label: 'Break', tone: 'text-teal-600 bg-teal-50 border-teal-200' },
  chapter: { icon: BookOpen, label: 'Study', tone: 'text-slate-600 bg-slate-100 border-slate-200' },
  concept: { icon: BookOpen, label: 'Study', tone: 'text-slate-600 bg-slate-100 border-slate-200' },
  topic: { icon: BookOpen, label: 'Study', tone: 'text-slate-600 bg-slate-100 border-slate-200' },
};

export const metaFor = (item) => ITEM_META[item?.node_type] || ITEM_META.chapter;
export { Clock3 };

export const DAY_KIND = {
  study: { label: 'Study day', tone: 'border-slate-200' },
  catchup: { label: 'Catch-up day', tone: 'border-indigo-200 bg-indigo-50/40' },
  revision: { label: 'Revision', tone: 'border-sky-200 bg-sky-50/40' },
  subject_revision: { label: 'Subject revision', tone: 'border-sky-300 bg-sky-50/60' },
  final_revision: { label: 'Final push', tone: 'border-rose-200 bg-rose-50/40' },
  mock: { label: 'Mock day', tone: 'border-rose-200 bg-rose-50/40' },
};

export const dayLabel = (day) => (DAY_KIND[day?.kind] || DAY_KIND.study).label;
export const dayTone = (day) => (DAY_KIND[day?.kind] || DAY_KIND.study).tone;

export const fmtDay = (iso, opts = { weekday: 'short', day: 'numeric', month: 'short' }) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', opts);

export const isWeekendIso = (iso) => [0, 6].includes(new Date(`${iso}T00:00:00`).getDay());

/** Clock times for a day's items: school evenings start at 5 PM, free days at
 * 9:30 AM, with a 15-minute breather between blocks. */
export function withClockTimes(day) {
  if (!day) return [];
  let cursor = isWeekendIso(day.date) ? 9 * 60 + 30 : 17 * 60;
  return (day.items || []).map((item) => {
    const isRecall = item.node_type === 'recall';
    const start = isRecall ? 21 * 60 + 45 : cursor;
    if (!isRecall) cursor = start + (item.minutes || 0) + 15;
    const h24 = Math.floor(start / 60) % 24;
    const mm = String(start % 60).padStart(2, '0');
    const suffix = h24 >= 12 ? 'PM' : 'AM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return { ...item, clock: `${h12}:${mm} ${suffix}` };
  });
}

export const prettyMinutes = (m) => (m >= 60
  ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}`
  : `${m} min`);

// Pure calendar maths in UTC — local-midnight + toISOString() slips a day in IST.
export const addDays = (iso, delta) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + delta)).toISOString().slice(0, 10);
};

/** Monday-first week containing `iso`. */
export const weekOf = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  const shift = (d.getDay() + 6) % 7;
  const monday = addDays(iso, -shift);
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
};
